import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { ChevronLeft, ChevronRight, Copy, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import CtaButton from "@/components/CtaButton";
import { Badge } from "@/components/ui/badge";
import { CoberturaZonasGrid, CeldaZona, inicialesZona } from "@/components/ZonasCobertura";
import DuplicarZonaMenu from "@/components/DuplicarZonaMenu";

function lunesDe(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

const ZonasSemanal = forwardRef(function ZonasSemanal(
  { onPublicar, onEditTramo, onAddTramo, onDuplicateTramo, onDuplicateDia, onAssignFromCobertura, onEditTramoFromCobertura },
  ref
) {
  const [semanaInicio, setSemanaInicio] = useState(() => lunesDe(hoyISO()));
  const [data, setData] = useState(null);
  const [publicando, setPublicando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data: res } = await api.get("/zonas/planificacion/semanal", { params: { desde: semanaInicio } });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [semanaInicio]);

  useImperativeHandle(ref, () => ({ cargar }));

  useEffect(() => {
    cargar();
  }, [cargar]);

  const navegar = (delta) => {
    const d = new Date(`${semanaInicio}T12:00:00`);
    d.setDate(d.getDate() + delta * 7);
    setSemanaInicio(d.toISOString().slice(0, 10));
  };

  const publicarSemana = async () => {
    if (!data) return;
    if (!window.confirm(
      `¿Publicar los cambios del ${fmtFecha(data.desde)} al ${fmtFecha(data.hasta)}? Solo se notificará a quienes tengan cambios.`
    )) return;
    setPublicando(true);
    try {
      const { data: res } = await api.post("/zonas/planificacion/publicar", { desde: data.desde, hasta: data.hasta });
      const n = res?.notificados ?? 0;
      toast.success(n > 0 ? `Publicado: ${n} trabajador${n !== 1 ? "es" : ""} notificado${n !== 1 ? "s" : ""}` : "Publicado (sin cambios que notificar)");
      cargar();
      onPublicar?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPublicando(false);
    }
  };

  const empConHorario = (emp, fechaDia) => {
    const dia = emp.dias?.find((d) => d.fecha === fechaDia);
    return { ...emp, horario: dia?.horario || emp.horario };
  };

  if (!data) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-fuchsia-200 border-t-fuchsia-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navegar(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Semana</p>
            <p className="text-sm font-semibold">{fmtFecha(data.desde)} – {fmtFecha(data.hasta)}</p>
          </div>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navegar(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSemanaInicio(lunesDe(hoyISO()))}>
            Hoy
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data.borradores_pendientes > 0 && (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
              {data.borradores_pendientes} cambio{data.borradores_pendientes !== 1 ? "s" : ""} sin publicar
            </Badge>
          )}
          <CtaButton className="gap-1.5" onClick={publicarSemana} disabled={publicando}>
            <Upload className="h-4 w-4" />
            {publicando ? "Publicando…" : "Publicar horarios"}
          </CtaButton>
        </div>
      </div>

      {data.zonas?.length > 0 && (
        <div className="flex flex-wrap gap-3 rounded-xl border border-fuchsia-100 bg-gradient-to-r from-fuchsia-50/80 to-violet-50/80 px-4 py-2 text-xs">
          {data.zonas.map((z) => (
            <span key={z.id} className="flex items-center gap-1.5 text-zinc-600">
              <span className="h-3 w-3 rounded" style={{ backgroundColor: z.color }} />
              {z.nombre}
            </span>
          ))}
        </div>
      )}

      <CoberturaZonasGrid
        dias={data.dias}
        horas={data.horas}
        coberturaZonas={data.cobertura_zonas}
        resumenLabel="asign. semana"
        onCellClick={(zona, fecha, info) => onAssignFromCobertura?.(zona, fecha, data.empleados, info)}
        onTramoClick={(tramo) => onEditTramoFromCobertura?.(tramo, data.empleados, tramo.fecha)}
      />

      <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] border-collapse text-xs">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="sticky left-0 z-10 min-w-44 bg-zinc-50 px-3 py-2 text-left font-semibold text-zinc-600">Empleado</th>
              {data.dias.map((d) => (
                <th key={d.fecha} className="min-w-[7.5rem] px-2 py-2 text-center font-medium text-zinc-500">
                  <span className="block text-[10px] text-violet-600">{d.nombre}</span>
                  <span className="text-[9px] text-zinc-400">{fmtFecha(d.fecha).slice(0, 5)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.empleados.map((emp) => (
              <tr key={emp.id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                <td className="sticky left-0 z-10 border-r border-zinc-100 bg-white px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[10px] font-bold text-violet-700">
                      {inicialesZona(emp.nombre)}
                    </span>
                    <div>
                      <p className="font-medium text-zinc-800">{emp.nombre}</p>
                      {emp.departamento && <p className="text-[10px] text-zinc-400">{emp.departamento}</p>}
                    </div>
                  </div>
                </td>
                {emp.dias.map((dia) => (
                  <td key={dia.fecha} className="align-top px-1 py-1.5">
                    <CeldaZona
                      dia={dia}
                      emp={emp}
                      fecha={dia.fecha}
                      onTramoClick={(e, t, f) => onEditTramo?.(empConHorario(e, f), t, f)}
                      onAdd={(e, f) => onAddTramo?.(empConHorario(e, f), f)}
                    />
                    {dia.laborable && !dia.ausencia && dia.tramos?.length > 0 && (onDuplicateDia || onDuplicateTramo) && (
                      <DuplicarZonaMenu
                        tramos={dia.tramos}
                        onDuplicarDia={onDuplicateDia
                          ? () => onDuplicateDia(empConHorario(emp, dia.fecha), dia.fecha, data.empleados)
                          : undefined}
                        onDuplicarTramo={onDuplicateTramo
                          ? (t) => onDuplicateTramo(empConHorario(emp, dia.fecha), [t.id], dia.fecha, data.empleados)
                          : undefined}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
            {!data.empleados.length && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-zinc-400">
                  No hay empleados activos en tu ámbito.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-zinc-500">
        Clic en la cobertura por zona, en una celda vacía o en un tramo para asignar o editar. Los cambios son borrador hasta publicar.
      </p>
    </div>
  );
});

export default ZonasSemanal;
